import type {
    VirtualAppointmentDto,
    VirtualAppointmentStatus,
    VirtualAppointmentTriggerKind
} from '../../services/virtualAppointments';
import { weeksCountStart, weeksDueDate } from './virtualAppointmentWeeks.ts';

export const OPEN_STATUS_OPTIONS = ['open', 'waiting_patient', 'waiting_doctor'] as const;
export type OpenVirtualAppointmentStatus = (typeof OPEN_STATUS_OPTIONS)[number];

// Statuses that keep a case in a "VA needed" work-queue step.
export const OPEN_VIRTUAL_APPOINTMENT_STATUSES: ReadonlySet<VirtualAppointmentStatus> = new Set(
    OPEN_STATUS_OPTIONS
);

export function isOpenStatus(
    status: VirtualAppointmentStatus
): status is OpenVirtualAppointmentStatus {
    return OPEN_VIRTUAL_APPOINTMENT_STATUSES.has(status);
}

export interface OpenVirtualAppointments {
    open: VirtualAppointmentDto[];
    /** Open, or done since the round started, in creation order so a card keeps its place. */
    round: VirtualAppointmentDto[];
    /** Planned, grouped by trigger kind (now, aligner, weeks, no-gos), soonest first within each. */
    planned: VirtualAppointmentDto[];
    /** Done before this round, or canceled; most recently resolved first. */
    history: VirtualAppointmentDto[];
}

function toTime(iso: string | null): number | null {
    if (iso === null) return null;
    const time = Date.parse(iso);
    return Number.isNaN(time) ? null : time;
}

// A round is the case's current VA episode, across both VA steps: a VA counts once resolved at or
// after the episode began.
export function isDoneThisRound(va: VirtualAppointmentDto, roundStartedAt: string | null): boolean {
    const start = toTime(roundStartedAt);
    const resolved = toTime(va.resolvedAt);
    return va.status === 'done' && start !== null && resolved !== null && resolved >= start;
}

// Done and canceled VAs both carry resolvedAt (a DB check enforces it on canceled); updatedAt only
// covers the nullable type.
export function closedAt(va: VirtualAppointmentDto): string {
    return va.resolvedAt ?? va.updatedAt;
}

function isPastVa(va: VirtualAppointmentDto, roundStartedAt: string | null): boolean {
    if (va.status === 'canceled') return true;
    return va.status === 'done' && !isDoneThisRound(va, roundStartedAt);
}

function byMostRecentlyClosed(a: VirtualAppointmentDto, b: VirtualAppointmentDto): number {
    return (toTime(closedAt(b)) ?? 0) - (toTime(closedAt(a)) ?? 0);
}

// A planned "now" VA is already due, so it leads.
const TRIGGER_KIND_ORDER: Record<VirtualAppointmentTriggerKind, number> = {
    now: 0,
    aligner: 1,
    weeks: 2,
    nogos: 3
};

// No-go VAs order by how many no-gos they still need; week VAs order by due day instead.
function triggerValue(va: VirtualAppointmentDto): number {
    switch (va.triggerKind) {
        case 'aligner':
            return va.triggerAligner ?? 0;
        case 'nogos':
            return (va.triggerNogos ?? 0) - va.nogoCount;
        case 'weeks':
        case 'now':
            return 0;
    }
}

function weeksDueOf(va: VirtualAppointmentDto, treatmentStartDate: string | null): string | null {
    return weeksDueDate(weeksCountStart(treatmentStartDate, va.triggerWeeksFrom), va.triggerWeeks);
}

// Days are YYYY-MM-DD, so they compare as strings; one with no day to count from goes last.
function byDueDay(a: string | null, b: string | null): number {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a < b ? -1 : 1;
}

// Triggers of different kinds can't be compared, so each kind is ordered on its own.
function byUpcoming(treatmentStartDate: string | null) {
    return (a: VirtualAppointmentDto, b: VirtualAppointmentDto): number => {
        const byKind = TRIGGER_KIND_ORDER[a.triggerKind] - TRIGGER_KIND_ORDER[b.triggerKind];
        if (byKind !== 0) return byKind;
        if (a.triggerKind !== 'weeks') return triggerValue(a) - triggerValue(b);
        return byDueDay(weeksDueOf(a, treatmentStartDate), weeksDueOf(b, treatmentStartDate));
    };
}

// One fetch serves the open list, this round's cards, the planned and past ones; split client-side.
export function partitionOpenVirtualAppointments(
    vas: readonly VirtualAppointmentDto[],
    roundStartedAt: string | null = null,
    treatmentStartDate: string | null = null
): OpenVirtualAppointments {
    return {
        open: vas.filter((va) => isOpenStatus(va.status)),
        round: vas.filter((va) => isOpenStatus(va.status) || isDoneThisRound(va, roundStartedAt)),
        planned: vas.filter((va) => va.status === 'planned').sort(byUpcoming(treatmentStartDate)),
        history: vas.filter((va) => isPastVa(va, roundStartedAt)).sort(byMostRecentlyClosed)
    };
}
